import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Edit, Trash2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SkillFrameworkDialog } from "./SkillFrameworkDialog";

interface SkillFramework {
  id: string;
  name: string;
  description: string;
  is_default: boolean;
  skill_categories: Array<{
    id: string;
    name: string;
    category_type: string;
    skills: Array<{
      id: string;
      name: string;
    }>;
  }>;
}

export const SkillFrameworksManagement = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedFramework, setSelectedFramework] = useState<SkillFramework | null>(null);

  const { data: frameworks, isLoading, refetch } = useQuery({
    queryKey: ['admin-skill-frameworks'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('skill_frameworks')
        .select(`
          id,
          name,
          description,
          is_default,
          skill_categories(
            id,
            name,
            category_type,
            skills(id, name)
          )
        `)
        .order('name');

      if (error) throw error;
      return data as SkillFramework[];
    },
  });

  const filteredFrameworks = frameworks?.filter(framework =>
    framework.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    framework.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEdit = (framework: SkillFramework) => {
    setSelectedFramework(framework);
    setIsDialogOpen(true);
  };

  const handleAdd = () => {
    setSelectedFramework(null);
    setIsDialogOpen(true);
  };

  const handleDelete = async (frameworkId: string) => {
    if (!confirm("Are you sure you want to delete this framework?")) return;

    try {
      const { error } = await supabase
        .from('skill_frameworks')
        .delete()
        .eq('id', frameworkId);

      if (error) throw error;
      refetch();
    } catch (error) {
      console.error('Error deleting framework:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 flex-1 max-w-md">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search frameworks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Button onClick={handleAdd}>
          <Plus className="h-4 w-4 mr-2" />
          Add Framework
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Frameworks</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{frameworks?.length || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Default Framework</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {frameworks?.filter(f => f.is_default).length || 0}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Skills</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {frameworks?.reduce((acc, f) => 
                acc + f.skill_categories.reduce((catAcc, cat) => catAcc + cat.skills.length, 0), 0) || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Skill Frameworks ({filteredFrameworks?.length || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Categories</TableHead>
                <TableHead>Skills</TableHead>
                <TableHead>Default</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredFrameworks?.map((framework) => (
                <TableRow key={framework.id}>
                  <TableCell className="font-medium">{framework.name}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {framework.skill_categories.map((category) => (
                        <Badge key={category.id} variant="outline" className="text-xs">
                          {category.name} ({category.category_type})
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    {framework.skill_categories.reduce((acc, cat) => acc + cat.skills.length, 0)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={framework.is_default ? "default" : "secondary"}>
                      {framework.is_default ? "Yes" : "No"}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-md">
                    <div className="truncate" title={framework.description || ''}>
                      {framework.description}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(framework)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(framework.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <SkillFrameworkDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        framework={selectedFramework}
        onSuccess={() => {
          refetch();
          setIsDialogOpen(false);
        }}
      />
    </div>
  );
};